import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { bridesTopProducts, Product, categoryDescriptions } from '../data/recommendations';
import Navbar from './Navbar';
import recommendationHero from '../data/recommendation-hero.png';

const TopProductsPage: React.FC = () => {
    // Scroll to top on mount
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Group products by category
    const groupedProducts = useMemo(() => {
        return bridesTopProducts.reduce((acc, product) => {
            if (!acc[product.category]) {
                acc[product.category] = [];
            }
            acc[product.category].push(product);
            return acc;
        }, {} as Record<string, Product[]>);
    }, []);

    const categories = Object.keys(groupedProducts);

    return (
        <div className="bg-white min-h-screen font-sans text-gray-800">
            <Navbar />

            {/* Hero Section */}
            <div className="relative pt-20 pb-16 md:pt-32 md:pb-24 bg-soft-cream overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img
                        src="https://images.unsplash.com/photo-1596462502278-27bfdd403348?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80"
                        alt="Background"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="inline-block py-1 mt-4 px-3 rounded-full bg-island-sage text-white text-xs font-semibold tracking-wide uppercase mb-4">
                            Bridal Beauty Guide
                        </span>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-deep-teal mb-6">
                            Top Skincare Products We Recommend
                        </h1>

                        <div className="flex justify-center mb-6">
                            <img
                                src={recommendationHero}
                                alt="Beauty Recommendation"
                                className="w-64 h-64 md:w-80 md:h-80 rounded-full object-cover border-4 border-white shadow-lg"
                            />
                        </div>

                        <p className="text-sm text-gray-500 mb-8 font-medium">
                            Published on {new Date().toLocaleDateString()}
                        </p>
                        <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                            Preparing for your big day involves more than just the dress. We've curated a list of top-rated beauty essentials to ensure your skin and hair are glowing when you walk down the aisle. From hydration heroes to puffiness fighters, here are our honest recommendations.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Intro Content & Disclaimer */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 text-center">
                <p className="text-lg md:text-xl text-deep-teal font-medium mb-6 leading-relaxed">
                    Every bride's skin is different, so we always recommend testing these products at least 3-4 weeks before your wedding day to ensure no sensitivity issues. Starting a consistent routine now is the best investment you can make for flawless wedding photos!
                </p>
                <div className="inline-block bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800 max-w-2xl mx-auto">
                    <strong>Note:</strong> Brides should pick products that they are the most familiar with and know works for their skin. This comes with trial and error.
                </div>
            </div>

            {/* Comparison Table Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-warm-sand">
                    <div className="p-6 bg-deep-teal text-white">
                        <h2 className="text-2xl font-serif font-semibold">Quick Comparison Guide</h2>
                        <p className="opacity-80">Our top picks at a glance</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 text-xs sm:text-sm uppercase tracking-wider">
                                    <th className="p-3 border-b">Product</th>
                                    <th className="p-3 border-b hidden sm:table-cell">Category</th>
                                    <th className="p-3 border-b hidden md:table-cell">Best For</th>
                                    <th className="p-3 border-b">Rating</th>
                                    <th className="p-3 border-b">Price</th>
                                    <th className="p-3 border-b">View</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {bridesTopProducts.slice(0, 10).map((product) => (
                                    <tr key={product.id} className="hover:bg-soft-cream/50 transition-colors">
                                        <td className="p-3 font-medium text-gray-900 flex items-center space-x-3">
                                            <img src={product.image} alt="" className="w-10 h-10 rounded-md object-cover shadow-sm bg-gray-200 flex-shrink-0" />
                                            <span className="line-clamp-2">{product.name}</span>
                                        </td>
                                        <td className="p-3 text-gray-600 hidden sm:table-cell">{product.category}</td>
                                        <td className="p-3 text-gray-600 hidden md:table-cell">{product.bestFor}</td>
                                        <td className="p-3 text-island-sage font-bold">{product.rating}/5</td>
                                        <td className="p-3 text-gray-600 font-medium">{product.price}</td>
                                        <td className="p-3">
                                            {product.links.length > 0 ? (
                                                <a href={product.links[0].url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-sunset-coral hover:bg-opacity-90 shadow-sm whitespace-nowrap">
                                                    {product.links[0].label === 'Amazon' ? 'Amazon' : 'View'}
                                                </a>
                                            ) : (
                                                <span className="text-xs text-gray-400 italic">Check Store</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-4 bg-gray-50 text-center text-xs text-gray-500">
                        Swipe to see more products if on mobile.
                    </div>
                </div>

                {/* Minimal Disclaimer */}
                <div className="mt-4 text-xs text-gray-400 text-center italic">
                    Disclosure: This article contains affiliate links. We may earn a small commission at no extra cost to you.
                </div>
            </div>

            {/* Main Content - Categories */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-16">
                {categories.map((category) => (
                    <section key={category} id={category.replace(/\s+/g, '-').toLowerCase()}>
                        <div className="text-center mb-8">
                            <h2 className="text-2xl md:text-3xl font-serif font-bold text-deep-teal mb-3">{category}</h2>
                            <p className="text-gray-600 max-w-2xl mx-auto italic text-sm md:text-base leading-relaxed">
                                {categoryDescriptions[category]}
                            </p>
                        </div>

                        <div className="grid gap-6">
                            {groupedProducts[category].map((product) => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.4 }}
                                    className="bg-white rounded-lg shadow-md border border-warm-sand overflow-hidden flex flex-col sm:flex-row h-full"
                                >
                                    {/* Image Side - Compact */}
                                    <div className="sm:w-40 md:w-48 h-48 sm:h-auto relative bg-white flex-shrink-0 p-4 flex items-center justify-center">
                                        {product.links.length > 0 ? (
                                            <a href={product.links[0].url} target="_blank" rel="noopener noreferrer" className="w-full h-full block">
                                                <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-300"
                                                />
                                            </a>
                                        ) : (
                                            <img
                                                src={product.image}
                                                alt={product.name}
                                                className="w-full h-full object-contain"
                                            />
                                        )}
                                    </div>

                                    {/* Content Side - Compact */}
                                    <div className="flex-1 p-4 sm:p-5 flex flex-col justify-between">
                                        <div>
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-0.5">{product.brand}</p>
                                                    <h3 className="text-lg md:text-xl font-serif font-bold text-gray-900 leading-tight">{product.name}</h3>
                                                </div>
                                                <div className="flex items-center text-island-sage font-bold text-sm bg-soft-cream px-1.5 py-0.5 rounded">
                                                    <span className="mr-1">{product.rating}</span>
                                                    <svg className="w-3 h-3 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                                                </div>
                                            </div>

                                            <p className="text-sm text-gray-600 mb-4 line-clamp-2 md:line-clamp-none">
                                                {product.description}
                                            </p>

                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-4">
                                                <div className="text-xs">
                                                    <span className="font-semibold text-gray-900 block mb-1">Pros</span>
                                                    <ul className="space-y-0.5 text-gray-600">
                                                        {product.pros.slice(0, 2).map((pro, i) => (
                                                            <li key={i} className="flex items-center">
                                                                <span className="text-green-500 mr-1.5 text-[10px]">✓</span> {pro}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div className="text-xs">
                                                    <span className="font-semibold text-gray-900 block mb-1">Cons</span>
                                                    <ul className="space-y-0.5 text-gray-600">
                                                        {product.cons.slice(0, 1).map((con, i) => (
                                                            <li key={i} className="flex items-center">
                                                                <span className="text-red-400 mr-1.5 text-[10px]">×</span> {con}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-2 flex space-x-3">
                                            {product.links.map((link, i) => (
                                                <a
                                                    key={i}
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded text-white bg-sunset-coral hover:bg-opacity-90 shadow-sm transition-all"
                                                >
                                                    Buy on {link.label} - {product.price}
                                                </a>
                                            ))}
                                            {product.links.length === 0 && (
                                                <div className="flex-1 text-center py-2 text-sm text-gray-500 bg-gray-50 rounded border border-gray-200">
                                                    Available in Stores ({product.price})
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            {/* Conclusion */}
            <div className="bg-warm-sand/30 py-16 mt-12">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="text-3xl font-serif font-bold text-deep-teal mb-4">Bottom Line</h2>
                    <p className="text-gray-600 mb-8 leading-relaxed">
                        We hope this guide helps you find the perfect additions to your beauty routine. Remember, the best glow comes from feeling confident and happy on your special day.
                    </p>
                    <h3 className="text-xl font-bold text-gray-800 mb-6">Need personalized advice?</h3>
                    <a
                        href="/contact"
                        className="inline-block px-8 py-3 bg-deep-teal text-white rounded-full font-medium shadow-lg hover:bg-opacity-90 transition-transform hover:scale-105"
                    >
                        Book a Consultation
                    </a>
                </div>
            </div>

        </div>
    );
};

export default TopProductsPage;
